import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useWebSocket } from "@/hooks/useWebSocket";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContentUploadModal } from "@/components/ContentUploadModal";
import { RepurposingUrlModal } from "@/components/RepurposingUrlModal";
import { WelcomeVideoModal } from "@/components/WelcomeVideoModal";
import { shouldAutoOpenWelcomeVideo, mountsLegacyTourAndVideo } from "@/lib/onboardingUiFlags";
import { UploadReviewCard } from "@/components/UploadReviewCard";
import { OnboardingProgressCard } from "@/components/dashboard/OnboardingProgressCard";
import { HeroPhotoToContent } from "@/components/dashboard/HeroPhotoToContent";
import { HeroRepurposingContent } from "@/components/dashboard/HeroRepurposingContent";
import { HeroVisualBrandCreator } from "@/components/dashboard/HeroVisualBrandCreator";
import { HeroVideoCreator } from "@/components/dashboard/HeroVideoCreator";
import { LinaBriefingHero } from "@/components/dashboard/LinaBriefingHero";
import { HotelMarketCard } from "@/components/dashboard/HotelMarketCard";
import { ToolboxSection } from "@/components/dashboard/ToolboxSection";
import { GoalsTeaser } from "@/components/dashboard/GoalsTeaser";
import { StatusHeader } from "@/components/cockpit/StatusHeader";
import { ChannelAlert } from "@/components/cockpit/ChannelAlert";
import { WeekStrip } from "@/components/cockpit/WeekStrip";
import { WirkungPanel } from "@/components/cockpit/WirkungPanel";
import { LinaBriefingPanel } from "@/components/cockpit/LinaBriefingPanel";
import { WeeklyTaskCard } from "@/components/cockpit/WeeklyTaskCard";
import { PillarsTile } from "@/components/cockpit/PillarsTile";
import { VisualBrandModal } from "@/components/VisualBrandModal";
import { DashboardTour } from "@/components/DashboardTour";
import { AlertCircle } from "lucide-react";
import type { User, Company, ContentUpload } from "@shared/schema";
import type { DashboardOverview, LinaTaskAction, SuggestionAction } from "@shared/dashboard";
import { StrategyRefineBanner } from "@/components/StrategyRefineBanner";
import { useHotelModule, useIsHotelEdition } from "@/lib/hotelEdition";

// The Cockpit (formerly "Dashboard"). Two modes switched by account state:
// new users keep the onboarding checklist + how-to cards; returning users get the
// status page — status header, week strip, Wirkung, Lina's briefing, one task, pillars.

const TASK_SKIP_PREFIX = "cockpit:task-skip:";

// Urgent tasks are never hidden by a skip; the nudges are keyed by week + action so a
// skipped "upload photos" cannot suppress a later, different suggestion in the same week.
const NEVER_SKIPPABLE: ReadonlySet<SuggestionAction> = new Set<SuggestionAction>(["review-posts", "connect-platform"]);

function taskSkipKey(weekIso: string, action: SuggestionAction): string {
  return `${TASK_SKIP_PREFIX}${weekIso}:${action}`;
}

function readTaskSkipped(weekIso: string | undefined, action: SuggestionAction | undefined): boolean {
  if (!weekIso || !action || NEVER_SKIPPABLE.has(action)) return false;
  try {
    return localStorage.getItem(taskSkipKey(weekIso, action)) === "1";
  } catch {
    return false;
  }
}

export default function Dashboard() {
  const [, setLocation] = useLocation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRepurposingModal, setShowRepurposingModal] = useState(false);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [showVisualBrandModal, setShowVisualBrandModal] = useState(false);
  const [autoplayUnmuted, setAutoplayUnmuted] = useState(false);
  const [devModeFirstTimeUser, setDevModeFirstTimeUser] = useState<boolean | null>(null);
  const [devModeProgress, setDevModeProgress] = useState<number | null>(null);
  const [manualTourTrigger, setManualTourTrigger] = useState(false);
  const [taskSkipped, setTaskSkipped] = useState<string | null>(null); // `${weekIso}:${action}`

  const { data: user } = useQuery<User>({
    queryKey: ['/api/auth/me']
  });

  const { data: company, isFetched: companyFetched } = useQuery<Company>({
    queryKey: ['/api/companies/me']
  });
  // Hotel-Edition (PLAN §5.1 items 1 and 4): label, no link/video/Brand-Visuals entry points (extra modules).
  const isHotel = useIsHotelEdition();
  // Hotels: "Sie" without a first name in every greeting (Mike 2026-10-10); agencies unchanged.
  const greetName = isHotel ? "" : (user?.firstName || "");
  const hasThemenkatalog = useHotelModule("themen");

  const { data: rawOverview, isError: overviewError, refetch: refetchOverview } = useQuery<DashboardOverview>({
    queryKey: ['/api/dashboard/overview'],
    enabled: !!user
  });

  const { data: pendingUploads = [] } = useQuery<ContentUpload[]>({
    queryKey: ['/api/uploads/my-pending'],
    enabled: !!user,
    refetchInterval: 5000 // Refresh every 5 seconds to catch new uploads
  });

  // Override with dev mode values
  const overview = import.meta.env.DEV && devModeProgress !== null && rawOverview
    ? { ...rawOverview, onboardingProgress: devModeProgress }
    : rawOverview;

  const { toast } = useToast();

  // Tour completion callback (must be memoized to prevent infinite useEffect loops)
  const handleTourComplete = useCallback(() => {
    toast({
      title: "Tour abgeschlossen!",
      description: "Sie kennen sich jetzt aus. Viel Erfolg mit Ihrem A-Team!",
    });
  }, [toast]);

  // Tour start callback - resets manual trigger to prevent re-runs
  const handleTourStart = useCallback(() => {
    setManualTourTrigger(false);
  }, []);

  // Initialize Dev Mode state from localStorage
  useEffect(() => {
    if (import.meta.env.DEV) {
      const savedDevMode = localStorage.getItem('devModeFirstTimeUser');
      if (savedDevMode !== null) {
        setDevModeFirstTimeUser(savedDevMode === 'true');
      }

      const savedProgress = localStorage.getItem('devModeProgress');
      if (savedProgress !== null) {
        setDevModeProgress(parseInt(savedProgress, 10));
      }
    }
  }, []);

  // Per-viewer task skip, keyed by ISO week + action (the weekly-briefing slice moves this server-side).
  const weekIso = overview?.cockpit?.status.weekIso;
  const suggestedAction = overview?.briefing.suggestion?.action;
  useEffect(() => {
    if (weekIso && suggestedAction && readTaskSkipped(weekIso, suggestedAction)) {
      setTaskSkipped(`${weekIso}:${suggestedAction}`);
    }
  }, [weekIso, suggestedAction]);

  // Listen for manual tour start event from sidebar
  useEffect(() => {
    const handleStartTour = () => {
      setManualTourTrigger(true);
    };

    window.addEventListener('startDashboardTour', handleStartTour);
    return () => window.removeEventListener('startDashboardTour', handleStartTour);
  }, []);

  // Listen for manual welcome video start event from sidebar
  useEffect(() => {
    const handleStartVideo = () => {
      setAutoplayUnmuted(true); // Sidebar click = user interaction, so we can unmute
      setShowWelcomeVideo(true);
    };

    window.addEventListener('startWelcomeVideo', handleStartVideo);
    return () => window.removeEventListener('startWelcomeVideo', handleStartVideo);
  }, []);

  useEffect(() => {
    let hasSeenVideo: string | null = null;
    try {
      hasSeenVideo = localStorage.getItem('hasSeenWelcomeVideo');
    } catch {
      hasSeenVideo = "1"; // storage blocked → never auto-play
    }
    // Auto-open is off for all customers (onboardingUiFlags); the sidebar entry still opens it.
    if (shouldAutoOpenWelcomeVideo(hasSeenVideo, user?.isOnboarded)) {
      setShowWelcomeVideo(true);
    }
  }, [user]);

  useWebSocket({
    onUploadCompleted: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-pending'] });
      toast({
        title: "Neuer Upload empfangen!",
        description: `${data.filename} wurde hochgeladen - bitte überprüfen!`
      });
    },
    onUploadReviewed: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-approved'] });
      toast({
        title: data.status === 'approved' ? "Upload freigegeben!" : "Upload abgelehnt",
        description: `${data.filename} wurde ${data.status === 'approved' ? 'freigegeben' : 'abgelehnt'}`
      });
    },
    onAuthSuccess: () => {}
  });

  // Dev Mode Override: Allow toggling First-Time User state
  const isFirstTimeUser = import.meta.env.DEV && devModeFirstTimeUser !== null
    ? devModeFirstTimeUser
    : overview?.mode === "new";

  const handleSuggestion = useCallback((action: SuggestionAction) => {
    switch (action) {
      case "upload-photo":
        setShowUploadModal(true);
        break;
      case "add-url":
        setShowRepurposingModal(true);
        break;
      case "review-posts":
        setLocation("/veroeffentlichungsplan");
        break;
      case "open-wochenplan":
        setLocation("/wochenplan");
        break;
      case "connect-platform":
        setLocation("/platform-connections");
        break;
    }
  }, [setLocation]);

  const handleTaskSkip = useCallback(() => {
    if (!weekIso || !suggestedAction) return;
    setTaskSkipped(`${weekIso}:${suggestedAction}`);
    try {
      localStorage.setItem(taskSkipKey(weekIso, suggestedAction), "1");
    } catch {
      /* per-viewer convenience only */
    }
  }, [weekIso, suggestedAction]);

  // Lina's stored weekly task: skip is persisted server-side (per company + week).
  const skipStoredTask = useMutation({
    mutationFn: async (week: string) => apiRequest("POST", "/api/dashboard/task/skip", { weekIso: week }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/overview'] });
    },
    onError: () => {
      toast({ title: "Konnte nicht überspringen", description: "Bitte versuchen Sie es gleich noch einmal.", variant: "destructive" });
    },
  });

  const handleLinaTask = useCallback((action: LinaTaskAction) => {
    if (action === "upload-photo") setShowUploadModal(true);
    else if (action === "open-strategy") setLocation("/marketingstrategie");
  }, [setLocation]);

  const handleDevModeToggle = () => {
    const newState = !devModeFirstTimeUser;
    setDevModeFirstTimeUser(newState);
    localStorage.setItem('devModeFirstTimeUser', String(newState));

    toast({
      title: "🔧 Dev Mode Gewechselt",
      description: newState
        ? "First-Time User Modus aktiviert - Seite wird neu geladen..."
        : "Normal Modus aktiviert - Seite wird neu geladen...",
    });

    // Reload page after 1 second to show new state
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const clearDevMode = () => {
    localStorage.removeItem('devModeFirstTimeUser');
    localStorage.removeItem('devModeProgress');
    setDevModeFirstTimeUser(null);
    setDevModeProgress(null);
    toast({
      title: "🔧 Dev Mode Zurückgesetzt",
      description: "Zurück zum normalen Modus - Seite wird neu geladen...",
    });
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleProgressChange = (progress: number) => {
    setDevModeProgress(progress);
    localStorage.setItem('devModeProgress', String(progress));
    toast({
      title: `🔧 Progress Mock: ${progress}%`,
      description: "Onboarding-Progress wurde geändert",
    });
  };

  if (overviewError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" aria-hidden="true" />
          <div>
            <h2 className="text-lg font-semibold">Cockpit konnte nicht geladen werden</h2>
            <p className="text-sm text-muted-foreground">Bitte versuchen Sie es erneut.</p>
          </div>
          <Button onClick={() => refetchOverview()}>Erneut versuchen</Button>
        </div>
      </div>
    );
  }

  // `cockpit` is additive on the contract; a payload without it (should never happen once
  // the server ships, but the client must not white-screen on it) renders the loading state.
  if (!user || !overview || !overview.cockpit) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Lade Cockpit...</div>
      </div>
    );
  }

  const cockpit = overview.cockpit;
  const suggestion = overview.briefing.suggestion;
  const linaBriefing = cockpit.linaBriefing;
  const storedTask = linaBriefing?.task && !linaBriefing.task.skipped && !linaBriefing.task.done ? linaBriefing.task : null;
  // Fallback while no stored briefing exists for the week: the template suggestion (slice 1 behaviour).
  const showSuggestionTask =
    !isFirstTimeUser &&
    !linaBriefing &&
    !!suggestion &&
    !(isHotel && suggestion.action === "add-url") &&
    (NEVER_SKIPPABLE.has(suggestion.action) || taskSkipped !== `${cockpit.status.weekIso}:${suggestion.action}`);

  const pendingUploadsBlock = pendingUploads.length > 0 && (
    <div className="mb-8" data-testid="pending-uploads-section">
      <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                {pendingUploads.length} {pendingUploads.length === 1 ? 'Foto' : 'Fotos'} warten auf Freigabe
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Prüfen und genehmigen Sie Ihre hochgeladenen Fotos, damit Ihr Team daraus Beiträge erstellt.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {pendingUploads.map((upload) => (
              <UploadReviewCard key={upload.id} upload={upload} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-full bg-background w-full overflow-x-hidden">
      <div className="mx-auto w-full max-w-[1180px] px-3 sm:px-5 py-5 sm:py-7">

        {/* Dev Mode Toggle - Only in Development */}
        {import.meta.env.DEV && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-end gap-2">
              <Button
                variant={devModeFirstTimeUser !== null ? "default" : "outline"}
                size="sm"
                onClick={handleDevModeToggle}
                data-testid="button-dev-toggle"
                className="gap-2"
                style={{ minHeight: '44px' }}
              >
                🔧
                {devModeFirstTimeUser
                  ? "First-Time User"
                  : devModeFirstTimeUser === false
                    ? "Returning User"
                    : "Normal Modus"}
              </Button>
              {devModeFirstTimeUser !== null && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDevMode}
                  data-testid="button-dev-clear"
                  title="Dev Mode zurücksetzen"
                  style={{ minHeight: '44px', minWidth: '44px' }}
                >
                  ✕
                </Button>
              )}
            </div>

            {devModeFirstTimeUser !== null && (
              <Card className="bg-amber-50 dark:bg-amber-950 border-2 border-amber-500">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                      🔧 Onboarding Progress Mock:
                    </span>
                    <div className="flex gap-2">
                      {[0, 33, 66, 100].map((progress) => (
                        <Button
                          key={progress}
                          variant={devModeProgress === progress ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleProgressChange(progress)}
                          data-testid={`button-progress-${progress}`}
                          className="min-w-[60px]"
                          style={{ minHeight: '44px' }}
                        >
                          {progress}%
                        </Button>
                      ))}
                    </div>
                    {devModeProgress !== null && (
                      <Badge variant="outline" className="ml-2">
                        Aktiv: {devModeProgress}%
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {isHotel && (
          <p className="mb-4 text-sm font-semibold text-foreground" data-testid="cockpit-edition-label">
            Social Media A-Team · <span className="text-primary">Hotel-Edition</span>
          </p>
        )}

        <StrategyRefineBanner userId={user.id} />

        {isFirstTimeUser ? (
          <>
            {/* New users: welcome, checklist, Lina's greeting and the four how-to cards */}
            <div className="mb-6">
              <h1 className="font-playfair text-3xl font-bold tracking-tight text-foreground">
                {greetName ? `Willkommen, ${greetName}.` : "Willkommen."}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">Ihr Cockpit füllt sich, sobald Ihr Team loslegt.</p>
            </div>

            {overview.onboardingProgress < 100 && (
              <div className="mb-8" data-testid="card-onboarding-progress">
                <OnboardingProgressCard
                  progress={overview.onboardingProgress}
                  onUploadClick={() => setShowUploadModal(true)}
                />
              </div>
            )}

            <LinaBriefingHero
              companyId={company?.id || ''}
              userId={user.id}
              userName={greetName}
              briefing={overview.briefing}
              onSuggestion={handleSuggestion}
              profileMissing={companyFetched && !company}
            />

            <div data-tour="four-pillars">
              {/* Row 1: Green modules */}
              <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="hero-content-creators">
                <HeroPhotoToContent onUploadClick={() => setShowUploadModal(true)} hotel={isHotel} />
                {!isHotel && <HeroRepurposingContent onRepurposingClick={() => setShowRepurposingModal(true)} />}
              </div>

              {/* Row 2: Purple modules (extra modules for hotels) */}
              {!isHotel && <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="hero-creative-tools">
                <HeroVisualBrandCreator onCreateClick={() => setShowVisualBrandModal(true)} />
                <HeroVideoCreator />
              </div>}
            </div>

            {pendingUploadsBlock}

            <div className="mb-8" data-testid="goals-teaser">
              <GoalsTeaser />
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-6" data-testid="cockpit-returning">
            {/* Returning users: the Cockpit */}
            <StatusHeader
              status={cockpit.status}
              wirkung={cockpit.wirkung}
              pendingApprovals={cockpit.pendingApprovals}
            />

            {cockpit.channelAlert && <ChannelAlert alert={cockpit.channelAlert} />}

            {pendingUploadsBlock}

            <WeekStrip
              days={cockpit.days}
              waiting={cockpit.waiting}
              onEmptyDayClick={() => setShowUploadModal(true)}
            />

            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1.35fr_1fr]">
              <WirkungPanel wirkung={cockpit.wirkung} />
              <LinaBriefingPanel
                paragraphs={linaBriefing ? linaBriefing.paragraphs : [overview.briefing.text]}
                signal={linaBriefing?.signal ?? null}
                companyId={company?.id || ''}
                userId={user.id}
                userName={greetName}
                profileMissing={companyFetched && !company}
              />
            </div>

            {storedTask ? (
              <WeeklyTaskCard
                action={storedTask.action}
                title={storedTask.title}
                reason={storedTask.reason}
                ctaLabel={storedTask.action === "upload-photo" ? "Fotos hochladen" : "Fragen beantworten"}
                onCta={() => handleLinaTask(storedTask.action)}
                onSkip={() => linaBriefing && skipStoredTask.mutate(linaBriefing.weekIso)}
                skipPending={skipStoredTask.isPending}
                hotel={isHotel}
              />
            ) : showSuggestionTask && suggestion ? (
              <WeeklyTaskCard
                action={suggestion.action}
                ctaLabel={suggestion.label}
                onCta={() => handleSuggestion(suggestion.action)}
                onSkip={handleTaskSkip}
                hotel={isHotel}
              />
            ) : null}

            <PillarsTile pillars={cockpit.pillars} hotel={isHotel} themenkatalog={hasThemenkatalog} />

            <HotelMarketCard />

            <ToolboxSection
              hiddenIds={isHotel ? ["url", "visuals", "video"] : []}
              onShortcutClick={(id) => {
                if (id === 'photo') setShowUploadModal(true);
                else if (id === 'url') setShowRepurposingModal(true);
                else if (id === 'visuals') setShowVisualBrandModal(true);
              }}
            />
          </div>
        )}

        {user && (
          <ContentUploadModal
            open={showUploadModal}
            onOpenChange={setShowUploadModal}
            user={user}
          />
        )}

        <RepurposingUrlModal
          open={showRepurposingModal}
          onOpenChange={setShowRepurposingModal}
        />

        <VisualBrandModal
          open={showVisualBrandModal}
          onOpenChange={setShowVisualBrandModal}
        />

        {/* Hotels: no old welcome video and no old Cockpit tour at all (mountsLegacyTourAndVideo). */}
        {mountsLegacyTourAndVideo(isHotel) && (<>
        <WelcomeVideoModal
          isOpen={showWelcomeVideo}
          onClose={() => {
            setShowWelcomeVideo(false);
            setAutoplayUnmuted(false); // Reset for next time
            try {
              localStorage.setItem('hasSeenWelcomeVideo', 'true');
            } catch {
              /* storage blocked */
            }
          }}
          autoplayUnmuted={autoplayUnmuted}
        />

        {/* Dashboard Tour - Auto-starts for first-time users after video, or on manual trigger */}
        <DashboardTour
          isFirstTimeUser={!!isFirstTimeUser}
          onboardingProgress={overview?.onboardingProgress ?? 0}
          onTourComplete={handleTourComplete}
          manualTrigger={manualTourTrigger}
          onTourStart={handleTourStart}
          videoShowing={showWelcomeVideo}
        />
        </>)}
      </div>
    </div>
  );
}
